import { FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useDeleteNavMutation } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { BaseModalProps, Button, ModalContentWrapper, TextInputField } from 'ui';
import { AlertIcon } from 'ui/icons';
import { DeleteNavByIdRequest } from 'api';

const validationSchema = (isActualAndHistoricalPrice: boolean) => {
  return yup.object<DeleteNavByIdRequest>({
    id: yup.number().required('This field is required'),
    justification: isActualAndHistoricalPrice
      ? yup.string().trim().required('This field is required')
      : yup.string().trim().nullable(),
  });
};

export interface IssuerAddNavModalProps extends BaseModalProps {
  id: number;
  isActualAndHistoricalPrice: boolean;
}

export const IssuerDeleteNavModal = ({
  open,
  onClose,
  id,
  isActualAndHistoricalPrice,
}: IssuerAddNavModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const formik = useFormik<DeleteNavByIdRequest>({
    initialValues: { justification: '', id },
    validationSchema: validationSchema(isActualAndHistoricalPrice),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      await deleteNav(values);
    },
    enableReinitialize: true,
  });

  const { isLoading, mutateAsync: deleteNav } = useDeleteNavMutation({ onSuccess: onClose });

  return (
    <ModalContentWrapper
      open={open}
      title={`Confirm ${getRegisterTypeStrings('nav')} Deletion`}
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            {isActualAndHistoricalPrice && (
              <span className="text-sm leading-[22px] font-[Inter]">
                Please add your justification (will be shown in Update Log and Investor
                notifications)
              </span>
            )}
            {isActualAndHistoricalPrice && (
              <TextInputField
                name="justification"
                label="Your justification for historical NAV change"
                placeholder="Enter your text"
                textarea={true}
              />
            )}
          </div>
          <div className="w-full flex items-center gap-6">
            <Button variant="secondary" onClick={onClose}>
              No
            </Button>
            <Button onClick={formik.handleSubmit} loading={isLoading} disabled={isLoading}>
              Yes
            </Button>
          </div>
          <div
            style={{ backgroundColor: 'rgba(3, 105, 161, 0.08)' }}
            className="flex gap-3 px-4 py-2 rounded"
          >
            <img src={AlertIcon} alt="info" />
            Deleting this {getRegisterTypeStrings('nav')} is permanent, but you can always create a
            new one if needed.
          </div>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
